import {
  type CSSProperties,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Editor, type EditorProps } from 'react-draft-wysiwyg';

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import { MenuItem, Paper } from '@mui/material';
import {
  CompositeDecorator,
  ContentBlock,
  EditorState,
  Modifier,
  SelectionState,
} from 'draft-js';
import type Typo from 'typo-js';

import {
  SpellSuggestContext,
  useSpellSuggest,
  type SpellSuggestRequestContext,
} from '@/contexts/SpellSuggestContext';
import { useTypo } from '@/hooks/useTypo';
import {
  cachedCheck,
  cachedSuggest,
  prefetchSuggestions,
  CUSTOM_ALLOWLIST,
} from '@/utils/typo';

import TextAlignButtons from '../customButtons/TextAlignButtons';
import TimestampButton from '../customButtons/TimestampButton';
import ToolbarVoiceButton from '../customButtons/ToolbarVoiceButton';
import { DefaultEditorConfig } from '../data';

export interface SpellCheckEditorProps {
  editorState: EditorState;
  onEditorStateChange: (state: EditorState) => void;
  editorStyle?: CSSProperties;
  mention?: EditorProps['mention'];
  toolbarCustomButtons?: ReactElement[];
  stripPastedStyles?: boolean;
  placeholder?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  readOnly?: boolean;
}

// ─── Spell-check internals ──────────────────────────────────────────────────

interface MisspelledWordProps {
  children: ReactNode;
  decoratedText: string;
  blockKey: string;
  start: number;
  end: number;
}

const MISSPELLED_STYLE: CSSProperties = {
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='6' height='3'%3E%3Cpath d='M0 3 Q1.5 0 3 3 Q4.5 6 6 3' stroke='red' fill='none' stroke-width='1'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'repeat-x',
  backgroundPosition: 'left bottom',
  backgroundSize: '6px 3px',
  paddingBottom: '3px',
  cursor: 'default',
};

const MisspelledWord = ({
  children,
  decoratedText,
  blockKey,
  start,
  end,
}: MisspelledWordProps) => {
  const { onRequestSuggestions, onLeaveSuggestions } = useSpellSuggest();

  return (
    <span
      onMouseEnter={(e) => {
        onRequestSuggestions({
          word: decoratedText,
          rect: e.currentTarget.getBoundingClientRect(),
          blockKey,
          start,
          end,
        });
      }}
      onMouseLeave={onLeaveSuggestions}
      style={MISSPELLED_STYLE}
    >
      {children}
    </span>
  );
};

interface CursorPosition {
  blockKey: string;
  offset: number;
  isCollapsed: boolean;
}

const createSpellCheckStrategy =
  (typo: Typo, selectionRef: MutableRefObject<CursorPosition | null>) =>
  (
    contentBlock: ContentBlock,
    callback: (start: number, end: number) => void,
  ) => {
    const text = contentBlock.getText();

    const currentSelection = selectionRef.current;
    const cursorOffset =
      currentSelection?.isCollapsed &&
      currentSelection.blockKey === contentBlock.getKey()
        ? currentSelection.offset
        : null;

    const wordRegex = /[a-zA-Z']+/g;
    let match: RegExpExecArray | null;
    while ((match = wordRegex.exec(text)) !== null) {
      const word = match[0];
      const start = match.index;
      const end = start + word.length;

      if (word.length < 3 || word.length > 50 || word === word.toUpperCase()) {
        continue;
      }
      if (CUSTOM_ALLOWLIST.has(word.toLowerCase())) {
        continue;
      }

      if (
        cursorOffset !== null &&
        cursorOffset >= start &&
        cursorOffset <= end
      ) {
        continue;
      }

      if (!cachedCheck(typo, word)) {
        callback(start, end);
        prefetchSuggestions(typo, word);
      }
    }
  };

const SHOW_DELAY_MS = 250;
const HIDE_DELAY_MS = 200;

// ─── SpellCheckEditor ───────────────────────────────────────────────────────

export const SpellCheckEditor = ({
  editorState,
  onEditorStateChange,
  editorStyle,
  mention,
  toolbarCustomButtons,
  stripPastedStyles = true,
  placeholder,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  readOnly = false,
}: SpellCheckEditorProps) => {
  const editorWrapperRef = useRef<HTMLDivElement | null>(null);
  const [suggestion, setSuggestion] = useState<{
    word: string;
    options: string[];
    rect: DOMRect;
    blockKey: string;
    start: number;
    end: number;
  } | null>(null);

  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;
  const suggestionRef = useRef(suggestion);
  suggestionRef.current = suggestion;
  const selectionRef = useRef<CursorPosition | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const typo = useTypo();

  const portalContainer = useMemo(() => {
    const el = document.createElement('div');
    el.style.setProperty('pointer-events', 'auto', 'important');
    return el;
  }, []);

  useEffect(() => {
    document.body.appendChild(portalContainer);
    return () => {
      if (document.body.contains(portalContainer)) {
        document.body.removeChild(portalContainer);
      }
    };
  }, [portalContainer]);

  const customDecorators = useMemo(
    () =>
      typo
        ? [
            {
              strategy: createSpellCheckStrategy(typo, selectionRef),
              component: MisspelledWord,
            },
          ]
        : [],
    [typo],
  );

  const cancelTimers = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const handleRequestSuggestions = useCallback(
    (req: SpellSuggestRequestContext) => {
      if (!typo) {
        return;
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
      }
      showTimerRef.current = setTimeout(() => {
        const options = cachedSuggest(typo, req.word);
        if (options.length > 0) {
          setSuggestion({ ...req, options });
        }
      }, SHOW_DELAY_MS);
    },
    [typo],
  );

  const handleLeaveSuggestions = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    hideTimerRef.current = setTimeout(() => {
      setSuggestion(null);
    }, HIDE_DELAY_MS);
  }, []);

  const handlePopupMouseEnter = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const handlePopupMouseLeave = useCallback(() => {
    setSuggestion(null);
  }, []);

  const handleReplace = useCallback(
    (replacement: string) => {
      const current = suggestionRef.current;
      if (!current) {
        return;
      }
      cancelTimers();
      const state = editorStateRef.current;
      const selectionState = SelectionState.createEmpty(current.blockKey).merge(
        {
          anchorOffset: current.start,
          focusOffset: current.end,
        },
      ) as SelectionState;

      const newContent = Modifier.replaceText(
        state.getCurrentContent(),
        selectionState,
        replacement,
      );

      const newState = EditorState.push(state, newContent, 'insert-characters');
      onEditorStateChange(newState);
      setSuggestion(null);
    },
    [cancelTimers, onEditorStateChange],
  );

  const suggestContextValue = useMemo(
    () => ({
      typo,
      onRequestSuggestions: handleRequestSuggestions,
      onLeaveSuggestions: handleLeaveSuggestions,
    }),
    [typo, handleRequestSuggestions, handleLeaveSuggestions],
  );

  const handleEditorStateChange = useCallback(
    (newEditorState: EditorState) => {
      const sel = newEditorState.getSelection();
      const newBlockKey = sel.getAnchorKey();
      const prevBlockKey = selectionRef.current?.blockKey;

      selectionRef.current = {
        blockKey: newBlockKey,
        offset: sel.getAnchorOffset(),
        isCollapsed: sel.isCollapsed(),
      };

      let stateToSet = newEditorState;
      if (typo && prevBlockKey && prevBlockKey !== newBlockKey) {
        const decorator = new CompositeDecorator([
          {
            strategy: createSpellCheckStrategy(typo, selectionRef),
            component: MisspelledWord,
          },
        ]);
        stateToSet = EditorState.set(newEditorState, { decorator });
      }

      onEditorStateChange(stateToSet);
    },
    [onEditorStateChange, typo],
  );

  const handleEditorMainMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const wrapper = editorWrapperRef.current;
      if (!wrapper) {
        return;
      }

      const mainEl = wrapper.querySelector(
        '.rdw-editor-main',
      ) as HTMLElement | null;
      const contentEl = wrapper.querySelector(
        '.public-DraftEditor-content',
      ) as HTMLElement | null;

      if (!mainEl || !contentEl) {
        return;
      }

      const target = event.target as Node;
      if (!mainEl.contains(target) || contentEl.contains(target)) {
        return;
      }

      const contentRect = contentEl.getBoundingClientRect();
      if (event.clientY <= contentRect.bottom) {
        return;
      }

      event.preventDefault();

      const currentState = editorStateRef.current;
      const content = currentState.getCurrentContent();
      const lastBlock = content.getLastBlock();
      const endOffset = lastBlock.getLength();

      const endSelection = SelectionState.createEmpty(lastBlock.getKey()).merge(
        {
          anchorOffset: endOffset,
          focusOffset: endOffset,
          hasFocus: true,
        },
      ) as SelectionState;

      onEditorStateChange(
        EditorState.forceSelection(currentState, endSelection),
      );
      const editable = wrapper.querySelector(
        '[contenteditable="true"]',
      ) as HTMLElement | null;
      editable?.focus();
    },
    [onEditorStateChange],
  );

  return (
    <SpellSuggestContext.Provider value={suggestContextValue}>
      <div ref={editorWrapperRef} onMouseDown={handleEditorMainMouseDown}>
        <Editor
          key={typo ? 'with-spellcheck' : 'no-spellcheck'}
          readOnly={readOnly}
          toolbarHidden={readOnly}
          toolbar={DefaultEditorConfig.toolbar}
          toolbarCustomButtons={[
            <TextAlignButtons key="text-align" />,
            <TimestampButton key="timestamp" />,
            ...(toolbarCustomButtons || []),
            <ToolbarVoiceButton
              key="voice"
              isEditorFocused={isEditorFocused}
            />,
          ]}
          customDecorators={customDecorators}
          mention={{ ...DefaultEditorConfig.mention, ...mention }}
          stripPastedStyles={stripPastedStyles}
          spellCheck={false}
          editorState={editorState}
          editorStyle={editorStyle ?? { minHeight: 250, fontSize: '16px' }}
          onEditorStateChange={handleEditorStateChange}
          onFocus={() => {
            setIsEditorFocused(true);
            onFocusProp?.();
          }}
          onBlur={() => {
            setIsEditorFocused(false);
            onBlurProp?.();
          }}
          placeholder={placeholder}
        />
      </div>
      {suggestion &&
        suggestion.options.length > 0 &&
        createPortal(
          <Paper
            elevation={8}
            onMouseEnter={handlePopupMouseEnter}
            onMouseLeave={handlePopupMouseLeave}
            sx={{
              position: 'fixed',
              bottom: window.innerHeight - suggestion.rect.top + 4,
              left: suggestion.rect.left,
              zIndex: 99999,
              py: 0.5,
              minWidth: 120,
            }}
          >
            {suggestion.options.map((opt) => (
              <MenuItem
                key={opt}
                dense
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleReplace(opt);
                }}
              >
                {opt}
              </MenuItem>
            ))}
          </Paper>,
          portalContainer,
        )}
    </SpellSuggestContext.Provider>
  );
};
